"use client"

import { graphql } from "@/gql"
import type { NovaSession } from "@/types"
import { ControlledModal } from "../Dialog"
import { captureException } from "@sentry/nextjs"
import { useCallback, useEffect, useRef, useState } from "react"
import { ContributorsModalQuery } from "@/gql/graphql"
import { Spinner } from "@/elements"
import { CreativeCredit } from "../CreativeCredit"
import { CreativeCreditSkeleton } from "./CreativeCreditSkeleton"

graphql(`
  query ContributorsModal($projectId: ID!, $first: Int, $after: String) {
    creativeWorkPortfolioProject(id: $projectId) {
      id
      creatorRolesString
      creative {
        id
        __typename
        name
        avatarInitials
        avatar80w: avatarUrl(width: 80)
        ... on IndividualCreative {
          handle
        }
      }
      contributors(first: $first, after: $after) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            __typename
            id
            name
            avatar80w: avatarUrl(width: 80)
            avatarInitials
            rolesString
            ... on CreativeWorkPortfolioProjectCreativeContributor {
              id
              creative {
                id
                __typename
                publicVisibility
                ... on IndividualCreative {
                  handle
                }
              }
            }

            ...CreativeCredit
          }
        }
      }
    }
  }
`)

interface Props {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const controller = new AbortController()

const apiRoute = "/api/portfolio/project/credits"

export const ContributorsModal: React.FC<Props> = ({ projectId, open, onOpenChange }) => {
  const [projectData, setProjectData] = useState<ContributorsModalQuery | null>(null)
  const [opened, setOpened] = useState(false)
  const [cachedProjectId, setCachedProjectId] = useState("")
  const loadMoreRef = useRef(null)

  useEffect(() => {
    if (open) {
      setOpened(open)
    }
  }, [open])

  const fetchData = useCallback(async () => {
    let res
    const signal = controller.signal
    try {
      res = await fetch(
        `${apiRoute}?projectId=${projectId}&first=10&after=${projectData?.creativeWorkPortfolioProject?.contributors?.pageInfo ? projectData?.creativeWorkPortfolioProject?.contributors?.pageInfo?.endCursor : null}`,
        {
          signal,
        }
      )
    } catch (e) {
      captureException(e)
      return
    }
    const resJson = await res?.json()
    if (resJson?.data?.creativeWorkPortfolioProject) {
      setCachedProjectId(projectId)
      setProjectData((prevData: any) => {
        const newData = resJson?.data
        const newEdges = newData.creativeWorkPortfolioProject?.contributors?.edges
        const prevContributors = prevData?.creativeWorkPortfolioProject?.contributors?.edges
        const mergedEdges =
          prevContributors && prevContributors?.length > 0 ? [...prevContributors, ...newEdges] : newEdges

        const deduplicatedEdges =
          prevContributors && prevContributors?.length > 0
            ? mergedEdges.filter((edge: any, index: number, self: any) => {
                const contributorId = edge?.node?.id
                return contributorId && self.findIndex((c: any) => c?.node?.id === contributorId) === index
              })
            : mergedEdges

        return {
          creativeWorkPortfolioProject: {
            ...newData?.creativeWorkPortfolioProject,
            contributors: {
              ...newData?.creativeWorkPortfolioProject?.contributors,
              edges: deduplicatedEdges,
            },
          },
        }
      })
    }
  }, [projectId, projectData])

  useEffect(() => {
    if (opened && cachedProjectId !== projectId && projectId) {
      fetchData()
    }
  }, [cachedProjectId, fetchData, opened, projectData, projectId])

  const refCurrent = loadMoreRef?.current
  const pageInfo = projectData?.creativeWorkPortfolioProject?.contributors?.pageInfo

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const firstEntry = entries[0]
        if (firstEntry.isIntersecting && pageInfo?.hasNextPage) {
          fetchData()
        }
      },
      { threshold: 1.0 }
    )

    if (refCurrent) {
      observer.observe(refCurrent)
    }

    return () => {
      observer.disconnect()
    }
  }, [loadMoreRef, pageInfo?.endCursor, pageInfo?.hasNextPage, refCurrent])

  const creative =
    projectData?.creativeWorkPortfolioProject?.creative?.__typename === "IndividualCreative"
      ? projectData?.creativeWorkPortfolioProject?.creative
      : null

  return (
    <ControlledModal
      open={open}
      onOpenChange={onOpenChange}
      header={{
        title: "Credits",
        variant: "passive",
        fixed: true,
      }}
      body={
        <div className="max-w-screen flex h-full w-full flex-col overflow-y-scroll sm:w-[400px]">
          {!projectData || projectId !== cachedProjectId ? (
            <div className="flex h-full w-full flex-col gap-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <CreativeCreditSkeleton grid showSeparator key={index} />
              ))}
            </div>
          ) : (
            <>
              <div className="">
                <CreativeCredit
                  grid
                  itemClassName="pt-2"
                  showSeparator
                  creatorCredit={{
                    creatorRolesString: projectData?.creativeWorkPortfolioProject?.creatorRolesString || "",
                    name: creative?.name || "",
                    avatarInitials: creative?.avatarInitials || "",
                    avatar80w: creative?.avatar80w || "",
                    handle: creative?.handle || "",
                  }}
                />
              </div>
              {projectData?.creativeWorkPortfolioProject?.contributors?.edges?.map((edge, index) => {
                const isCorrectType =
                  edge?.node?.__typename === "CreativeWorkPortfolioProjectCreativeContributor" ||
                  edge?.node?.__typename === "CreativeWorkPortfolioProjectWriteInContributor"

                const contributor = isCorrectType ? edge?.node : null
                return (
                  <div key={edge?.node?.id} className="flex w-full flex-col">
                    <CreativeCredit contributor={contributor} grid showSeparator itemClassName="pt-2" />
                  </div>
                )
              })}
            </>
          )}
          {pageInfo?.hasNextPage && (
            <div className="relative mt-1 flex h-[20px] w-full flex-row items-center justify-center">
              <Spinner size="small" />
            </div>
          )}
          <div ref={loadMoreRef} className="h-[4px]" />
        </div>
      }
    />
  )
}
